import {
  RECORDER_INITIATED,
  SDK_RECORDING_CANCELED,
  SDK_RECORDING_COMPLETED,
  SDK_RECORD_BUTTON_CLICKED,
  SDK_UPLOAD_COMPLETED,
} from 'constants/events';
import { VIDEOS_LOOMS_PAGE } from 'constants/routes';
import * as analytics from 'utilities/analytics';
import { blockUnload, unblockUnload } from 'utilities/block-unload';
import { isWindows } from 'utilities/device';
import { openExtensionIfInstalled } from 'utilities/extension';
import { incrementMetric } from 'utilities/metrics';
import { isSdkSupported } from 'utilities/recordSdk/isSdkSupported';
import getSdkInstance from 'utilities/recordSdk/useCases/persistentRecord';

import { CHROME_EXTENSION, DESKTOP, SDK } from './constants';

export const startDesktopRecording = (source, waitForDesktopInitFromFlag) =>
  new Promise(resolve => {
    const isOnVideosPage = window.location.pathname === VIDEOS_LOOMS_PAGE;

    const waitForDesktopInit =
      isOnVideosPage && isWindows ? waitForDesktopInitFromFlag : 500;

    const listener = () => {
      // A short timeout is required here for 'document.hasFocus' to update after
      // the 'beforeunload' event is triggered, otherwise hasFocus will always return true.
      setTimeout(() => {
        // If the document still has focus it means the desktop app has failed to launch (MacOS only)
        if (!document.hasFocus()) {
          analytics.track(RECORDER_INITIATED, {
            source,
            recorder_type: DESKTOP,
          });
          incrementMetric('recorder.initiate', {
            recorder_type: DESKTOP,
            source,
          });

          resolve(true);
        } else {
          resolve(false);
        }
      }, waitForDesktopInit);
    };

    window.addEventListener('beforeunload', listener);

    window.location.href = `loomDesktop://`;

    setTimeout(
      () => window.removeEventListener('beforeunload', listener),
      5100
    );
  });

export const startChromeExtensionRecording = source =>
  new Promise(resolve => {
    // An extra window focus check is required for Windows before launching the Chrome extension
    if (isWindows && !document.hasFocus()) {
      return;
    }

    openExtensionIfInstalled('persistent-record', success => {
      if (success) {
        analytics.track(RECORDER_INITIATED, {
          source,
          recorder_type: CHROME_EXTENSION,
        });
        incrementMetric('recorder.initiate', {
          recorder_type: CHROME_EXTENSION,
          source,
        });

        resolve(true);
      }

      resolve(false);
    });
  });

let sdkButton = null;

const setupSDK = async options => {
  if (sdkButton) {
    return;
  }

  const { configureButton } = await getSdkInstance().waitOnInstance();

  sdkButton = configureButton({
    hooks: {
      onStart: () => {
        options?.onStart?.();
        analytics.track(SDK_RECORD_BUTTON_CLICKED, { source: options.source });
        incrementMetric('recorder.start_recording', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
      onCancel: () => {
        options?.onCancel?.();
        analytics.track(SDK_RECORDING_CANCELED, { source: options.source });
        incrementMetric('recorder.cancel_recording', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
      onRecordingComplete: oembed => {
        blockUnload();
        options?.onRecordingComplete?.(oembed);
        window.open(oembed.sharedUrl + '?from_recorder=1', '_blank');
        analytics.track(SDK_RECORDING_COMPLETED, {
          source: options.source,
          videoUrl: oembed.sharedUrl,
          videoId: oembed.id,
        });
        incrementMetric('recorder.recording_complete', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
      onUploadComplete: oembed => {
        unblockUnload();
        options?.onUploadComplete?.(oembed);
        analytics.track(SDK_UPLOAD_COMPLETED, {
          source: options.source,
          videoUrl: oembed.sharedUrl,
          videoId: oembed.id,
        });
        incrementMetric('recorder.upload_complete', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
    },
  });
};

export const startSDKRecording = async options => {
  const { supported: isSDKSupported } = await isSdkSupported();

  if (isSDKSupported) {
    await setupSDK(options);
    analytics.track(RECORDER_INITIATED, {
      source: options.source,
      recorder_type: SDK,
    });
    incrementMetric('recorder.initiate', {
      recorder_type: SDK,
      source: options.source,
      isLoggedIn: options.isLoggedIn,
    });
    sdkButton.openPreRecordPanel();

    return true;
  }

  return false;
};
